import React, {
  memo,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import classNames from 'classnames';
import { fabric } from 'fabric';
import 'src/fabric-overrides';
import useUserPicture from '@pages/MediaViewer/AssetViewer/Canvas/hooks/useUserPicture';
import { pinCreate } from '@pages/MediaViewer/AssetViewer/Canvas/Objects/pinHandlers';
import { fitPinSize } from '@pages/MediaViewer/AssetViewer/Canvas/utils/fitObject';
import {
  getAssetAnnotationZoom,
  setAnnotationZoom
} from '@pages/MediaViewer/AssetViewer/Canvas/utils/annotationCoordinates';
import { enableCanvasPixelRatio } from '@pages/MediaViewer/AssetViewer/Canvas/utils/canvasPixelRatio';
import {
  clientRectToElement,
  getScrollLayoutMargin,
  layoutAnnotationCanvas
} from '@pages/MediaViewer/AssetViewer/Canvas/utils/annotationLayout';
import { getCommentCursor } from '@pages/MediaViewer/AssetViewer/Canvas/utils/cursors';
import { updateDrawingToolCursorHint } from '@pages/MediaViewer/AssetViewer/Canvas/DrawingToolCursorHint/DrawingToolCursorHint';
import { renderPdfPageAnnotationsOnCanvas } from '@pages/MediaViewer/AssetViewer/PdfCard/utils/renderPdfPageAnnotationsOnCanvas';
import { FabricContext } from '@context/FabricContext';
import { useAssetViewerState } from '@hooks/useAssetViewerState';
import { useResponsive } from '@hooks/useResponsive';
import {
  PIN_DRAW_TOOL,
  isPinOrHighlightTarget
} from '@pages/MediaViewer/helpers/drawToolShape';

import './PdfPageDisplayCanvas.less';

type PdfPageDisplayCanvasProps = {
  pageNumber: number;
  pageWidth?: number;
  pageHeight?: number;
  zoomFactor: number;
  turnoverDegree: number;
  visible: boolean;
  interactive: boolean;
  isActive: boolean;
  /** The scroll container of the pages: the canvas covers the part of the page in view */
  scrollViewport?: Element | null;
  /** Lets a comment be placed while the page is not the active one */
  onActivatePage?: (pageNumber: number) => void;
};

function setCanvasCursor(canvas: fabric.Canvas, cursor: string) {
  canvas.defaultCursor = cursor;
  canvas.hoverCursor = cursor;
  if ((canvas as any).upperCanvasEl) {
    canvas.setCursor(cursor);
  }
}

function PdfPageDisplayCanvas({
  pageNumber,
  pageWidth,
  pageHeight,
  zoomFactor,
  turnoverDegree,
  visible,
  interactive,
  isActive,
  scrollViewport,
  onActivatePage
}: PdfPageDisplayCanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasElRef = useRef<HTMLCanvasElement>(null);
  const fabricCanvasRef = useRef<fabric.Canvas | null>(null);
  // The canvas is recreated when the page is resized: effects attaching to it
  // depend on this, so they attach to the new canvas too
  const [canvasVersion, setCanvasVersion] = useState(0);
  const {
    commentsList,
    selectedCommentId,
    isHideComments,
    selectComment,
    setActivePageNumber,
    setCanvasMode,
    setDocumentHighlight,
    shapesDrawingTools,
    isMobileDrawingToolbarOpen
  } = useAssetViewerState();
  const {
    canvas: mainCanvas,
    mouseOverObjectRef,
    objectToCreateRef,
    draftPinPlacedRef,
    cursorHintRef,
    clearCanvas
  } = useContext(FabricContext);
  const { getUserPicture, setMyPicture } = useUserPicture();
  const { isMdWidth } = useResponsive();
  const isCommentTool =
    shapesDrawingTools.shape === PIN_DRAW_TOOL &&
    (!isMdWidth || isMobileDrawingToolbarOpen);
  const shouldBlockTouchScroll = isCommentTool && isActive;

  const onSelectComment = useCallback(
    (commentId: string) => {
      selectComment(commentId);
      setActivePageNumber(pageNumber);
    },
    [pageNumber, setActivePageNumber, selectComment]
  );

  const layoutParamsRef = useRef({
    pageWidth,
    pageHeight,
    zoomFactor,
    turnoverDegree,
    scrollViewport
  });
  layoutParamsRef.current = {
    pageWidth,
    pageHeight,
    zoomFactor,
    turnoverDegree,
    scrollViewport
  };

  // Lays the canvas over the part of the page in view (and around it).
  // Without a scroll container, over the whole page.
  const layoutCanvas = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    const element = containerRef.current;
    // The page, rotated with CSS: the canvas is laid out in its coordinates
    const page = element?.offsetParent as HTMLElement | null | undefined;
    const params = layoutParamsRef.current;
    if (!canvas || !element || !page || !params.pageWidth || !params.pageHeight)
      return;

    const asset = {
      left: 0,
      top: 0,
      width: params.pageWidth,
      height: params.pageHeight
    };
    const viewportRect = params.scrollViewport?.getBoundingClientRect();
    const isRotated = params.turnoverDegree % 360 !== 0;
    const pageRect = isRotated ? null : page.getBoundingClientRect();
    layoutAnnotationCanvas({
      canvas,
      element,
      asset,
      visible: viewportRect
        ? clientRectToElement(viewportRect, page, params.turnoverDegree)
        : asset,
      zoom: getAssetAnnotationZoom(params.zoomFactor),
      margin: viewportRect ? getScrollLayoutMargin(viewportRect) : 0,
      snapOrigin: pageRect
        ? {
            left: pageRect.left + page.clientLeft,
            top: pageRect.top + page.clientTop
          }
        : undefined
    });
  }, []);

  useLayoutEffect(() => {
    layoutCanvas();
  }, [
    layoutCanvas,
    canvasVersion,
    pageWidth,
    pageHeight,
    zoomFactor,
    turnoverDegree,
    scrollViewport
  ]);

  useEffect(() => {
    if (!visible) return undefined;
    let frameId = 0;
    const scheduleLayout = () => {
      if (frameId) return;
      frameId = requestAnimationFrame(() => {
        frameId = 0;
        layoutCanvas();
      });
    };
    scrollViewport?.addEventListener('scroll', scheduleLayout, {
      passive: true
    });
    window.addEventListener('resize', scheduleLayout);
    return () => {
      cancelAnimationFrame(frameId);
      scrollViewport?.removeEventListener('scroll', scheduleLayout);
      window.removeEventListener('resize', scheduleLayout);
    };
  }, [visible, scrollViewport, layoutCanvas]);

  useLayoutEffect(() => {
    if (!visible || !canvasElRef.current || !pageWidth || !pageHeight) {
      return undefined;
    }

    const canvas = new fabric.Canvas(canvasElRef.current, {
      selectionColor: 'transparent',
      selectionLineWidth: 0,
      centeredScaling: true,
      targetFindTolerance: 5,
      preserveObjectStacking: true,
      perPixelTargetFind: true,
      // Enabled below, within a pixel budget
      enableRetinaScaling: false,
      // Sized by the layout, to the part of the page in view
      width: 1,
      height: 1,
      selection: false,
      allowTouchScrolling: true
    });
    enableCanvasPixelRatio(canvas);

    fabricCanvasRef.current = canvas;
    layoutCanvas();
    setCanvasVersion((version) => version + 1);

    return () => {
      canvas.dispose();
      fabricCanvasRef.current = null;
    };
  }, [visible, pageWidth, pageHeight, layoutCanvas]);

  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!visible || !canvas) return;
    canvas.allowTouchScrolling = !shouldBlockTouchScroll;
  }, [visible, shouldBlockTouchScroll, canvasVersion]);

  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!visible || !canvas || !pageWidth || !pageHeight) return;

    renderPdfPageAnnotationsOnCanvas({
      canvas,
      pageNumber,
      // The selected comment is shown by the main canvas, not twice
      commentsList: selectedCommentId
        ? commentsList.filter((comment) => comment.id !== selectedCommentId)
        : commentsList,
      zoomFactor,
      turnoverDegree,
      isHideComments,
      overObject: mouseOverObjectRef,
      getUserPicture,
      onSelectComment,
      viewerActions: {
        selectComment,
        setCanvasMode,
        setDocumentHighlight
      }
    });
  }, [
    visible,
    pageNumber,
    pageWidth,
    pageHeight,
    commentsList,
    selectedCommentId,
    zoomFactor,
    turnoverDegree,
    isHideComments,
    getUserPicture,
    mouseOverObjectRef,
    onSelectComment,
    selectComment,
    setCanvasMode,
    setDocumentHighlight
  ]);

  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!visible || !interactive || !canvas) return undefined;

    if (!isCommentTool) {
      setCanvasCursor(canvas, 'default');
      return undefined;
    }

    const pinCursor = getCommentCursor();
    setCanvasCursor(canvas, pinCursor);

    const onMouseMove = (event: fabric.IEvent<Event>) => {
      if (
        event.target &&
        isPinOrHighlightTarget(event.target as fabric.Object)
      ) {
        canvas.setCursor('pointer');
        updateDrawingToolCursorHint(cursorHintRef.current, { hidden: true });
        return;
      }
      canvas.setCursor(pinCursor);
      updateDrawingToolCursorHint(cursorHintRef.current, {
        shape: PIN_DRAW_TOOL,
        event: event.e as MouseEvent,
        hidden: draftPinPlacedRef.current
      });
    };
    const onMouseOut = () => {
      updateDrawingToolCursorHint(cursorHintRef.current, { hidden: true });
    };

    canvas.on('mouse:over', onMouseMove);
    canvas.on('mouse:move', onMouseMove);
    canvas.on('mouse:out', onMouseOut);
    return () => {
      canvas.off('mouse:over', onMouseMove);
      canvas.off('mouse:move', onMouseMove);
      canvas.off('mouse:out', onMouseOut);
      updateDrawingToolCursorHint(cursorHintRef.current, { hidden: true });
      setCanvasCursor(canvas, 'default');
    };
  }, [
    visible,
    interactive,
    isCommentTool,
    cursorHintRef,
    draftPinPlacedRef,
    canvasVersion
  ]);

  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (
      (!isActive && !onActivatePage) ||
      !interactive ||
      !visible ||
      !canvas ||
      !mainCanvas ||
      !isCommentTool
    ) {
      return undefined;
    }

    const onMouseDown = (e: fabric.IEvent) => {
      if (e.target || mouseOverObjectRef.current) return;
      if (!isActive) onActivatePage?.(pageNumber);

      const pointer = canvas.getPointer(e.e);

      draftPinPlacedRef.current = true;
      selectComment('new');
      setDocumentHighlight(null);
      setCanvasMode('edit');
      clearCanvas();

      // For the pin's size: the main canvas is laid out over this page once
      // shown (usePdfFabricCanvas)
      setAnnotationZoom(mainCanvas, getAssetAnnotationZoom(zoomFactor));

      const { email, name, picture } = setMyPicture();
      pinCreate({
        userPicture: picture,
        x: pointer.x,
        y: pointer.y,
        canvas: mainCanvas,
        overObject: mouseOverObjectRef,
        objectToCreate: objectToCreateRef,
        userEmail: email,
        userName: name,
        scale: fitPinSize(mainCanvas)
      });
    };

    canvas.on('mouse:down', onMouseDown);
    return () => {
      canvas.off('mouse:down', onMouseDown);
    };
  }, [
    isActive,
    onActivatePage,
    pageNumber,
    interactive,
    visible,
    mainCanvas,
    isCommentTool,
    selectComment,
    setDocumentHighlight,
    setCanvasMode,
    setMyPicture,
    clearCanvas,
    zoomFactor,
    mouseOverObjectRef,
    objectToCreateRef,
    draftPinPlacedRef,
    canvasVersion
  ]);

  if (!visible || !pageWidth || !pageHeight) return null;

  return (
    <div
      ref={containerRef}
      className={classNames('media_viewer_pdf-page-display-canvas', {
        'b-non-interactive': !interactive
      })}
    >
      <canvas ref={canvasElRef} />
    </div>
  );
}

export default memo(PdfPageDisplayCanvas);
